/**
 * Flota de demostración para probar en local las pantallas B2B con muchos dispositivos.
 * Es determinista (sin aleatoriedad) y la usan los repositorios en memoria de
 * dispositivos, heartbeats, oxygens e incidencias, que construyen sus datos a partir de ella.
 */

export const DEMO_FLEET_SIZE = 150;

export const DEMO_GROUP_IDS = {
	centro: '61ae5f1d-35d7-49d8-a719-6d5fc346227d',
	delicias: 'c5f6513f-991f-48d5-9cde-09ef2dda1ea5'
} as const;

export type DemoHealth = 'recent' | 'stale' | 'noPressure' | 'none';
export type DemoCapabilities = 'full' | 'noBloodPressure' | 'noHealth';
export type DemoIncidence = 'open' | 'closed' | 'none';

export type DemoDevice = {
	index: number;
	id: string;
	identificator: string;
	carrierName: string;
	battery: number;
	/** Milisegundos desde la última conexión, relativos a cuando se cargan los datos. */
	lastConnectionAgoMs: number;
	isDisconnect: boolean;
	capabilities: DemoCapabilities;
	groupId: string;
	health: DemoHealth;
	incidence: DemoIncidence;
};

const MINUTE = 60_000;
const DAY = 86_400_000;

const FIRST_NAMES = [
	'MARÍA',
	'JOSÉ',
	'CARMEN',
	'ANTONIO',
	'PILAR',
	'MANUEL',
	'DOLORES',
	'FRANCISCO',
	'ISABEL',
	'JESÚS',
	'ROSARIO',
	'ÁNGEL',
	'TERESA',
	'MIGUEL',
	'CONCEPCIÓN'
];

const LAST_NAMES = [
	'GARCÍA',
	'FERNÁNDEZ',
	'GONZÁLEZ',
	'RODRÍGUEZ',
	'LÓPEZ',
	'MARTÍNEZ',
	'SÁNCHEZ',
	'PÉREZ',
	'GÓMEZ',
	'MARTÍN',
	'JIMÉNEZ',
	'RUIZ',
	'HERNÁNDEZ',
	'DÍAZ',
	'MORENO',
	'ÁLVAREZ',
	'ROMERO'
];

function pad(value: number, length: number): string {
	return String(value).padStart(length, '0');
}

function buildDemoDevice(index: number): DemoDevice {
	return {
		index,
		id: `00000000-0000-4000-8000-${pad(index, 12)}`,
		identificator: `DEMO ${pad(index, 3)}`,
		carrierName: `${FIRST_NAMES[index % FIRST_NAMES.length]} ${LAST_NAMES[(index * 7) % LAST_NAMES.length]} ${LAST_NAMES[(index * 3 + 1) % LAST_NAMES.length]}`,
		battery: (index * 37) % 101,
		lastConnectionAgoMs: index % 10 === 0 ? 3 * DAY : ((index * 13) % 600) * MINUTE,
		isDisconnect: index % 10 === 0 || index % 17 === 0,
		capabilities: index % 9 === 0 ? 'noHealth' : index % 4 === 0 ? 'noBloodPressure' : 'full',
		groupId: index % 3 === 0 ? DEMO_GROUP_IDS.delicias : DEMO_GROUP_IDS.centro,
		health:
			index % 6 === 0
				? 'none'
				: index % 5 === 0
					? 'stale'
					: index % 7 === 0
						? 'noPressure'
						: 'recent',
		incidence:
			index % 8 === 0 || index % 11 === 0 ? 'open' : index % 13 === 0 ? 'closed' : 'none'
	};
}

export const demoFleet: DemoDevice[] = Array.from({ length: DEMO_FLEET_SIZE }, (_, offset) =>
	buildDemoDevice(offset + 1)
);

/**
 * Momento de la última medición de salud de un dispositivo de demo, relativo a `now`.
 * Devuelve null si el dispositivo no tiene datos de salud.
 */
export function demoLatestHealthAt(device: DemoDevice, now: number): number | null {
	if (device.health === 'none' || device.capabilities === 'noHealth') return null;
	if (device.health === 'stale') return now - 2 * DAY - device.index * MINUTE;
	return now - (((device.index * 7) % 120) + 5) * MINUTE;
}
